import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTools } from '@/hooks/useTools';
import { useUser } from '@/hooks/useUser';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  Archive,
  MessageSquare,
  FileSearch,
  FilePlus2,
  Bell,
  BookOpen,
  Zap,
  Wrench,
  TextSearch,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// ── Category colours ──────────────────────────────────────────────────────────
const CATEGORY_COLOURS: Record<string, string> = {
  Knowledge: 'bg-blue-50 border-blue-200',
  Meetings: 'bg-purple-50 border-purple-200',
  Monitoring: 'bg-amber-50 border-amber-200',
  Help: 'bg-green-50 border-green-200',
};

const CATEGORY_ICON_COLOUR: Record<string, string> = {
  Knowledge: 'text-blue-600 bg-blue-100',
  Meetings: 'text-purple-600 bg-purple-100',
  Monitoring: 'text-amber-600 bg-amber-100',
  Help: 'text-green-600 bg-green-100',
};

// Fallback icon map by slug (for seeded platform tools before DB has icons)
const SLUG_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  'knowledge-explorer': Archive,
  'on-the-record': MessageSquare,
  'meeting-summary': FileSearch,
  'document-qa': FilePlus2,
  'topic-monitor': Bell,
  'platform-guide': BookOpen,
  'deep-research-brief': Zap,
  'full-text-search': TextSearch,
};

// ── Knowledge sub-group ordering ──────────────────────────────────────────────
// Row 1 — direct retrieval (3-col)
const KNOWLEDGE_QUICK_SLUGS = [
  'on-the-record',
  'document-qa',
  'full-text-search',
];
// Row 2 — synthesis / discovery (2-col, wider cards)
const KNOWLEDGE_DEEP_SLUGS = ['knowledge-explorer', 'deep-research-brief'];

/** Friendly display name for engine values */
function engineLabel(engine: string | null | undefined): string {
  if (!engine) return '';
  return engine === 'native_llm' ? 'LLM' : engine;
}

export const ToolsCatalogPage: React.FC = () => {
  usePageTitle('Tools');
  const { tools, loading } = useTools();
  const { user } = useUser();
  const { currentWorkspace } = useWorkspaces();
  const navigate = useNavigate();

  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const isAdmin = user?.role === 'SYSTEM_ADMIN' || user?.role === 'ORG_OWNER';

  // Group by category
  const categories = ['Knowledge', 'Meetings', 'Monitoring', 'Help', 'Other'];
  const grouped = categories.reduce<Record<string, typeof tools>>(
    (acc, cat) => {
      acc[cat] = tools.filter((t) => (t.category ?? 'Other') === cat);
      return acc;
    },
    {}
  );

  const handleToolClick = (slug: string) => {
    const params = currentWorkspace?.id
      ? `?workspace=${currentWorkspace.id}`
      : '';
    navigate(`/tools/${slug}${params}`);
  };

  // ── Shared card renderer ──────────────────────────────────────────────────
  type ToolItem = (typeof tools)[0];
  const renderCard = (tool: ToolItem, cat: string) => {
    const Icon = SLUG_ICONS[tool.slug] ?? Wrench;
    const cardBg = CATEGORY_COLOURS[cat] ?? 'bg-gray-50 border-gray-200';
    const iconBg = CATEGORY_ICON_COLOUR[cat] ?? 'text-gray-600 bg-gray-100';
    return (
      <button
        key={tool.id}
        onClick={() => handleToolClick(tool.slug)}
        className={`group text-left rounded-xl border p-5 hover:shadow-md transition-shadow ${cardBg}`}
      >
        <div className="flex items-start gap-3 mb-3">
          <div className={`p-2 rounded-lg ${iconBg}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-sm truncate">
                {tool.name}
              </span>
              {isAdmin && (
                <Badge variant="outline" className="text-xs py-0">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {engineLabel((tool as any).engine ?? tool.type)}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-600 line-clamp-2">
          {tool.description ?? 'No description available.'}
        </p>
        <div className="mt-3 text-xs font-medium text-blue-600 group-hover:underline">
          Open →
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Tools</h1>
        <p className="text-gray-500 mt-1">
          Explore and run tools connected to your state/county/town's knowledge
          archive.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-40 rounded-xl bg-gray-100 animate-pulse"
            />
          ))}
        </div>
      ) : tools.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Wrench className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">
            No tools available yet
          </h3>
          <p className="text-sm text-gray-500 max-w-sm">
            {isAdmin
              ? 'Use the Admin → Tool Registry to create and enable tools for your organization.'
              : 'Contact your organization owner to get access to tools.'}
          </p>
        </div>
      ) : (
        categories.map((cat) => {
          const catTools = grouped[cat];
          if (!catTools || catTools.length === 0) return null;

          // ── Knowledge: two visual sub-rows ─────────────────────────────
          if (cat === 'Knowledge') {
            const quickTools = KNOWLEDGE_QUICK_SLUGS.map((slug) =>
              catTools.find((t) => t.slug === slug)
            ).filter((t): t is ToolItem => !!t);
            const deepTools = KNOWLEDGE_DEEP_SLUGS.map((slug) =>
              catTools.find((t) => t.slug === slug)
            ).filter((t): t is ToolItem => !!t);
            const otherTools = catTools.filter(
              (t) =>
                !KNOWLEDGE_QUICK_SLUGS.includes(t.slug) &&
                !KNOWLEDGE_DEEP_SLUGS.includes(t.slug)
            );

            return (
              <section key={cat}>
                <h2 className="text-base font-semibold text-gray-600 uppercase tracking-wider mb-4">
                  {cat}
                </h2>

                {/* Row 1 — Ask & Find */}
                {quickTools.length > 0 && (
                  <div className="mb-5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                      Ask &amp; Find
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      {quickTools.map((tool) => renderCard(tool, cat))}
                    </div>
                  </div>
                )}

                {quickTools.length > 0 && deepTools.length > 0 && (
                  <div className="border-t border-blue-100 mb-5" />
                )}

                {/* Row 2 — Deep Research (wider 2-col cards) */}
                {deepTools.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                      Deep Research
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {deepTools.map((tool) => renderCard(tool, cat))}
                    </div>
                  </div>
                )}

                {/* Overflow — any Knowledge tools not in the lists above */}
                {otherTools.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-4">
                    {otherTools.map((tool) => renderCard(tool, cat))}
                  </div>
                )}
              </section>
            );
          }

          // ── All other categories ────────────────────────────────────────
          return (
            <section key={cat}>
              <h2 className="text-base font-semibold text-gray-600 uppercase tracking-wider mb-3">
                {cat}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {catTools.map((tool) => renderCard(tool, cat))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
};

export default ToolsCatalogPage;
